import api from "../lib/api";

interface StreamReportParams {
  from?: string;
  to?: string;
  actionType?: string;
  courtroom?: string;
  cnr?: string;
  page?: number;
  limit?: number;
}
export interface LoginReportParams {
  from?: string;
  to?: string;
  role?: string;
  page?: number;
  limit?: number;
}
export interface UserReportParams {
  filter?: string;
  fromDate?: string;
  toDate?: string;
}

export interface RegistrationReportResponse {
  totalIndividual: number;
  totalAdvocate: number;
  total: number;
  graphData: {
    name: string;
    individual: number;
    advocate: number;
  }[];
  yearMonthGraphData?: {
    name: string;
    individual: number;
    advocate: number;
  }[];
}

export interface ArchivalReportParams {
  from?: string;
  to?: string;
  dateType?: "request" | "decision";
  status?: string;
  controller?: string;
  user?: string;
  page?: number;
  limit?: number;
}

//  Get all courtrooms
export const getAllCourtRooms = async () => {
  return api.get("/api/reports/get-courtrooms");
};

export const getAllCourtRoomsForController = async () => {
  return api.get("/api/reports/get-courtrooms-for-controller");
};

export const getAllControllers = async () => {
  return api.get("/api/reports/get-all-controllers");
};

export const getAllCaseNumbers = async () => {
  return api.get("/api/reports/get-caseNumber-list");
};
export const getProceedingDetails = async (id: string) => {
  return api.get(`/api/reports/get-proceeding-details/${id}`);
};

//  Get filtered daily list data
export const getDailyList = async (params: {
  courtRoomNo: string;
  date: string;
  caseStage?: string;
}) => {
  return api.get("/api/reports/get-daily-list", { params });
};

//  Export daily list as CSV
export const exportDailyListCSV = async (params: {
  courtRoomNo: string;
  date: string;
  caseStage?: string;
}) => {
  const query = new URLSearchParams(params as any).toString();
  return api.get(`/api/reports/daily-list/export/csv?${query}`, {
    responseType: "blob", // for file download
  });
};

//  Export daily list as Excel
export const exportDailyListExcel = async (params: {
  courtRoomNo: string;
  date: string;
  caseStage?: string;
}) => {
  const query = new URLSearchParams(params as any).toString();
  return api.get(`/api/reports/daily-list/export/excel?${query}`, {
    responseType: "blob",
  });
};

//  Export daily list as PDF
export const exportDailyListPDF = async (params: {
  courtRoomNo: string;
  date: string;
  caseStage?: string;
}) => {
  const query = new URLSearchParams(params as any).toString();
  return api.get(`/api/reports/daily-list/export/pdf?${query}`, {
    responseType: "blob",
  });
};

//Stream report api's
export const getStreamReport = async (body?: StreamReportParams) => {
  return api.post("/api/reports/get-stream-report", body);
};
export const exportStreamReportCSV = async (params?: any) => {
  return api.get("/api/reports/get-stream-report/export/csv", {
    params,
    responseType: "blob",
  });
};

export const exportStreamReportExcel = async (params?: any) => {
  return api.get("/api/reports/get-stream-report/export/excel", {
    params,
    responseType: "blob",
  });
};

export const exportStreamReportPDF = async (params?: any) => {
  return api.get("/api/reports/get-stream-report/export/pdf", {
    params,
    responseType: "blob",
  });
};
// Fetch user registration report
export const getUserReport = async (params?: UserReportParams) => {
  return api.get("/api/reports/get-user-report", { params });
};

// Download report (CSV / Excel / PDF)
export const downloadUserReport = async (
  type: "csv" | "excel" | "pdf",
  params?: UserReportParams,
) => {
  return api.get(`/api/reports/get-user-report/${type}`, {
    params,
    responseType: "blob",
  });
};

export const getLogginReport = async (params?: LoginReportParams) => {
  return api.get("/api/reports/login-reports", { params });
};

//  Export Login Activity Report as CSV
export const exportLoginReportCSV = async (params?: LoginReportParams) => {
  return api.get("/api/reports/login-reports/export/csv", {
    params,
    responseType: "blob",
  });
};

export const exportLoginReportExcel = async (params?: LoginReportParams) => {
  return api.get("/api/reports/login-reports/export/excel", {
    params,
    responseType: "blob",
  });
};

export const exportLoginReportPDF = async (params?: LoginReportParams) => {
  return api.get("/api/reports/login-reports/export/pdf", {
    params,
    responseType: "blob",
  });
};
export const getAllUsernames = () => api.get("/api/reports/get-all-usernames");

export const getUserRegistrationReport = async (
  filter: string = "all",
  fromDate?: string,
  toDate?: string,
): Promise<RegistrationReportResponse> => {
  let url = `/api/reports/get-user-report?filter=${filter}`;

  if (filter === "custom") {
    if (fromDate) url += `&fromDate=${fromDate}`;
    if (toDate) url += `&toDate=${toDate}`;
  }

  const response = await api.get(url);
  return response.data.data;
};

export const getStreamViewersReport = async (params: {
  filter?: string;
  fromDate?: string;
  toDate?: string;
}) => {
  return api.get("/api/reports/get-stream-viewers-report", { params });
};

export const exportStreamViewersReportCSV = async (params?: any) =>
  api.get("/api/reports/get-stream-viewers-report/export/csv", {
    params,
    responseType: "blob",
  });

export const exportStreamViewersReportExcel = async (params?: any) =>
  api.get("/api/reports/get-stream-viewers-report/export/excel", {
    params,
    responseType: "blob",
  });

export const exportStreamViewersReportPDF = async (params?: any) =>
  api.get("/api/reports/get-stream-viewers-report/export/pdf", {
    params,
    responseType: "blob",
  });

export const getLiveSessionsReport = async (params?: any) =>
  api.get("/api/reports/get-live-sessions-report", { params });

export const exportLiveSessionsReportCSV = (params?: any) =>
  api.get("/api/reports/get-live-sessions-report/export/csv", {
    params,
    responseType: "blob",
  });

export const exportLiveSessionsReportExcel = async (params?: any) =>
  api.get("/api/reports/get-live-sessions-report/export/excel", {
    params,
    responseType: "blob",
  });

export const exportLiveSessionsReportPDF = async (params?: any) =>
  api.get("/api/reports/get-live-sessions-report/export/pdf", {
    params,
    responseType: "blob",
  });

export const getArchivalReport = async (params?: ArchivalReportParams) => {
  return api.get("/api/reports/archival-recordings-report", { params });
};

export const exportArchivalReportCSV = async (
  params?: ArchivalReportParams,
) => {
  return api.get("/api/reports/archival-recordings-report/export/csv", {
    params,
    responseType: "blob",
  });
};

export const exportArchivalReportExcel = async (
  params?: ArchivalReportParams,
) => {
  return api.get("/api/reports/archival-recordings-report/export/excel", {
    params,
    responseType: "blob",
  });
};

export const exportArchivalReportPDF = async (
  params?: ArchivalReportParams,
) => {
  return api.get("/api/reports/archival-recordings-report/export/pdf", {
    params,
    responseType: "blob",
  });
};
